import { useId, type ReactNode } from 'react';
import { Download, FileUp, ShoppingCart, Users } from 'lucide-react';
import { ApiError } from '@signalvote/api-client';
import { ErrorCode, RecipientSource, assertNever, type WorkOrderDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';

/** A one-column Recipient list to fill in, served from the web app's public folder. */
export const RECIPIENT_LIST_TEMPLATE_URL = '/templates/recipient-list-template.csv';

/** The person's answer to "who gets it". */
export type RecipientChoice = 'upload' | 'cellPhones' | 'buy';

/** Upload refusals in plain words, by their stable code. */
const UPLOAD_REFUSALS: Partial<Record<string, string>> = {
  [ErrorCode.RecipientFileRequired]: 'That file is empty. Choose a CSV with a column of phone numbers.',
  [ErrorCode.RecipientFileTooLarge]: 'That file is over 10 MB. Remove the columns you don’t need and try again.',
  [ErrorCode.NoPhoneColumn]: 'We couldn’t find a phone number column. Name the column “Phone” or “Cell” and try again.',
  [ErrorCode.NoValidRecipients]: 'None of the numbers in that file are valid US phone numbers.',
  [ErrorCode.RecipientUploadRequiresDraft]: 'This text is already on its way, so its list can’t change.',
};

export function recipientUploadErrorMessage(error: unknown): string | null {
  if (!error) return null;
  const refusal = error instanceof ApiError && error.code ? UPLOAD_REFUSALS[error.code] : undefined;
  return refusal ?? 'We couldn’t read that file. Try again.';
}

/**
 * The choice a saved Draft already answers, if any. A new Draft starts on the campaign's cell phones
 * without anyone choosing them, and a send that let go of a bought list goes back there, so that
 * source counts as chosen only once a send time is saved after it and there are cell phones to choose.
 */
export function savedRecipientChoice(workOrder: WorkOrderDto): RecipientChoice | null {
  switch (workOrder.details.recipientSource) {
    case RecipientSource.UploadedCsv:
      return 'upload';
    case RecipientSource.CampaignCellPhones:
      return workOrder.sendDate && (workOrder.recipientCount ?? 0) > 0 ? 'cellPhones' : null;
    case RecipientSource.BoughtList:
      return 'buy';
    default:
      return assertNever(workOrder.details.recipientSource);
  }
}

/** Recipients the Draft would send to for this choice, or null until the choice has a list. */
export function recipientsFor(workOrder: WorkOrderDto, choice: RecipientChoice | null): number | null {
  const source = workOrder.details.recipientSource;
  if (choice === 'upload' && source === RecipientSource.UploadedCsv) return workOrder.recipientCount;
  if (choice === 'cellPhones' && source === RecipientSource.CampaignCellPhones) return workOrder.recipientCount;
  if (choice === 'buy' && source === RecipientSource.BoughtList) return workOrder.recipientCount;
  return null;
}

export interface RecipientSourcePickerProps {
  workOrder: WorkOrderDto;
  choice: RecipientChoice | null;
  onChoiceChange: (choice: RecipientChoice) => void;
  /** Sets the Draft's source; called when the choice already has a list on the server. */
  onSourceChange: (source: RecipientSource) => void;
  onUpload: (file: File) => void;
  uploading: boolean;
  uploadError: string | null;
  /**
   * Building and pricing a list to buy, shown once Buy a list is picked; null while there's nothing to
   * show yet. Left out, buying is offered as coming soon.
   */
  buy?: ReactNode;
}

/**
 * Who gets the text: large options until one is picked, then a compact toggle above the picked
 * option's details, as the voter list builder does with a list's purpose.
 */
export function RecipientSourcePicker({
  workOrder,
  choice,
  onChoiceChange,
  onSourceChange,
  onUpload,
  uploading,
  uploadError,
  buy,
}: RecipientSourcePickerProps) {
  const id = useId();
  const { recipientSource, uploadedReadyCount, uploadedRemovedCount, campaignCellPhoneCount: cellPhones } = workOrder.details;

  const options = [
    { key: 'upload', icon: FileUp, title: 'I have numbers', sub: 'Upload a CSV with a phone column.' },
    { key: 'buy', icon: ShoppingCart, title: 'Buy a list', sub: buy === undefined ? 'Coming soon.' : 'Choose voters to text from the voter file.' },
    ...(cellPhones
      ? [{ key: 'cellPhones', icon: Users, title: 'Everyone in my voters with a cell phone', sub: null }]
      : []),
  ] as const;

  // Choosing an upload over a bought list takes the list out of the order at once, before any file is chosen.
  const dropsBoughtList = recipientSource === RecipientSource.BoughtList;
  const choose = (next: RecipientChoice) => {
    onChoiceChange(next);
    if (next === 'cellPhones' && recipientSource !== RecipientSource.CampaignCellPhones) {
      onSourceChange(RecipientSource.CampaignCellPhones);
    }
    if (next === 'upload' && recipientSource !== RecipientSource.UploadedCsv && (uploadedReadyCount !== null || dropsBoughtList)) {
      onSourceChange(RecipientSource.UploadedCsv);
    }
  };
  const showUploaded = recipientSource === RecipientSource.UploadedCsv && uploadedReadyCount !== null;

  return (
    <div className="space-y-6">
      <div
        role="radiogroup"
        aria-label="Who gets it"
        className={choice ? 'inline-flex flex-wrap gap-1 rounded-lg bg-muted p-1' : 'grid gap-3 sm:grid-cols-2'}
      >
        {options.map((option) => {
          const disabled = option.key === 'buy' && buy === undefined;
          const checked = choice === option.key;
          return (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => !disabled && choose(option.key as RecipientChoice)}
              className={cn(
                'text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none',
                choice
                  ? cn(
                      'rounded-md px-3 py-1.5 text-sm font-semibold',
                      checked ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )
                  : 'rounded-xl border border-border p-5 enabled:hover:border-primary enabled:active:scale-[0.99]',
              )}
            >
              {!choice && <option.icon className="mb-3 h-5 w-5 text-primary" aria-hidden="true" />}
              <span className={choice ? '' : 'block text-lg font-semibold text-foreground'}>{option.title}</span>
              {!choice && option.key === 'cellPhones' && (
                <span className="mt-1 block text-sm text-muted-foreground">
                  <span data-testid="cell-phone-count" className="font-semibold tabular-nums text-foreground">
                    {cellPhones.toLocaleString('en-US')}
                  </span>{' '}
                  voters already in your campaign.
                </span>
              )}
              {!choice && option.sub && <span className="mt-1 block text-sm text-muted-foreground">{option.sub}</span>}
            </button>
          );
        })}
      </div>

      {choice === 'cellPhones' && (
        <p className="text-[1.0625rem] leading-relaxed text-muted-foreground">
          Every voter in your campaign with a cell phone:{' '}
          <b data-testid="cell-phone-count" className="font-bold tabular-nums text-foreground">
            {cellPhones.toLocaleString('en-US')}
          </b>{' '}
          numbers.
        </p>
      )}

      {choice === 'buy' && buy}

      {choice === 'upload' && (
        <div className="space-y-3">
          {showUploaded && (
            <p data-testid="upload-counts" className="text-[1.0625rem] text-foreground">
              <b className="tabular-nums">{uploadedReadyCount.toLocaleString('en-US')}</b> ready ·{' '}
              <span className="tabular-nums">{(uploadedRemovedCount ?? 0).toLocaleString('en-US')}</span> removed
            </p>
          )}
          {showUploaded && (uploadedRemovedCount ?? 0) > 0 && (
            <p className="text-sm text-muted-foreground">
              We removed duplicates and numbers that aren’t valid. You won’t pay for those.
            </p>
          )}
          <label
            htmlFor={`${id}-csv`}
            className={cn(
              'block cursor-pointer rounded-xl border border-dashed border-border p-6 text-center transition-colors hover:border-primary focus-within:ring-2 focus-within:ring-ring',
              uploading && 'pointer-events-none opacity-60',
            )}
          >
            <span className="block text-[1.0625rem] text-foreground">
              {uploading ? 'Checking numbers…' : showUploaded ? 'Use a different file' : 'Choose your CSV'}
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">
              Only the phone column matters. We clean up the numbers for you.
            </span>
            <input
              id={`${id}-csv`}
              type="file"
              accept=".csv,text/csv"
              aria-label="CSV of phone numbers"
              disabled={uploading}
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onUpload(file);
              }}
            />
          </label>
          {uploadError && (
            <p role="alert" className="text-sm text-destructive">
              {uploadError}
            </p>
          )}
          <div className="space-y-1.5 text-sm text-muted-foreground">
            <a
              href={RECIPIENT_LIST_TEMPLATE_URL}
              download
              className="inline-flex items-center gap-1.5 font-medium text-primary underline-offset-4 hover:underline"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Download a template
            </a>
            <ul className="list-disc space-y-1 pl-5">
              <li>In Excel or Google Sheets, save it as CSV (File › Save As, or File › Download).</li>
              <li>Put the numbers in one column named Phone or Cell. Other columns are ignored.</li>
              <li>Any format works. We remove duplicates and numbers that aren’t valid, and you don’t pay for them.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
